import type {ScheduleState} from '../store/scheduleDuck'
import type {Schedule} from '../types/domain'
import type {TimerSnapshot} from './domain/timerSnapshot'
import {projectTimer} from './domain/timerSnapshot'
import {colorUp} from '../timer/chips/colorUpCheck'

export const scheduleFromState = (state: ScheduleState): Schedule => ({name: state.name, blinds: state.blinds, breaks: state.breaks, chips: state.chips, levelLength: state.levelLength})

export function snapshotFromState(state: ScheduleState, now: number): TimerSnapshot {
  const suddenDeath = !state.onBreak && state.currentBlindNumber >= state.blinds.length - 1
  return {
    ...(state.hasStarted === undefined ? {} : {hasStarted: state.hasStarted}),
    levelIndex: state.currentBlindNumber,
    breakIndex: state.nextBreakNumber,
    onBreak: state.onBreak,
    capturedAtMs: now,
    clock: suddenDeath ? {kind: 'suddenDeath'} : state.timerPlaying && state.timerDeadlineMs !== null && state.timerDeadlineMs !== undefined
      ? {kind: 'running', deadlineMs: state.timerDeadlineMs}
      : {kind: 'paused', remainingMs: state.timerRemainingMs ?? state.secondsLeftInCurrentSession * 1000},
  }
}

export function stateFromSnapshot(schedule: Schedule, timer: TimerSnapshot, now: number): Partial<ScheduleState> {
  const effective = projectTimer(timer, now)
  let chips = schedule.chips.map(chip => ({...chip, colorUp: false, hide: false}))
  for (let levelIndex = 1; levelIndex <= timer.levelIndex; levelIndex++) {
    const completedBreak = schedule.breaks.some((scheduledBreak, breakIndex) => breakIndex < timer.breakIndex && scheduledBreak.afterLevelNumber === levelIndex)
    if (completedBreak) chips = colorUp(chips, schedule.blinds, levelIndex, false)
    chips = colorUp(chips, schedule.blinds, levelIndex)
  }
  if (timer.onBreak) chips = colorUp(chips, schedule.blinds, timer.levelIndex + 1, false)
  return {
    hasStarted: timer.hasStarted,
    ...schedule, chips, currentBlindNumber: timer.levelIndex, nextBreakNumber: timer.breakIndex, onBreak: timer.onBreak,
    secondsLeftInCurrentSession: Math.ceil((effective.remainingMs ?? 0) / 1000),
    timerRemainingMs: effective.remainingMs ?? 0,
    timerPlaying: effective.playing,
    timerDeadlineMs: timer.clock.kind === 'running' ? timer.clock.deadlineMs : null,
  }
}

export function skipSnapshot(schedule: Schedule, timer: TimerSnapshot, now: number): TimerSnapshot {
  let next: TimerSnapshot
  if (timer.onBreak) next = {...timer, levelIndex: timer.levelIndex + 1, breakIndex: timer.breakIndex + 1, onBreak: false}
  else if (schedule.breaks[timer.breakIndex]?.afterLevelNumber === timer.levelIndex + 1) next = {...timer, onBreak: true}
  else next = {...timer, levelIndex: Math.min(timer.levelIndex + 1, schedule.blinds.length - 1)}
  const durationMs = (next.onBreak ? schedule.breaks[next.breakIndex].length : schedule.levelLength) * 60000
  next.clock = !next.onBreak && next.levelIndex === schedule.blinds.length - 1 ? {kind: 'suddenDeath'} : timer.clock.kind === 'running' ? {kind: 'running', deadlineMs: now + durationMs} : {kind: 'paused', remainingMs: durationMs}
  return {...next, capturedAtMs: now}
}
